import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { Calendar, Clock } from "lucide-react";
import { FaSpinner } from "react-icons/fa";
import toast from "react-hot-toast";
import * as HVTRegistrationService from "../../Services/HVTRegistration/HVTRegistrationService";
import {
    formatDateOnly,
    toPhleboGender,
    SLOT_PERIODS,
    groupSlotsByPeriod,
    formatSlotTimeDisplay,
} from "./HVTCommonHelpers";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";
import CommonConfirmationModal from "../../Common/CommonConfirmationModal";

const RESCHEDULE_REASONS = [
    "Patient not available at scheduled time",
    "Requested by patient",
    "Phlebotomist unavailable",
    "Address or location changed",
    "Other",
];

const MAX_SLOT_DATE = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

const HVTRescheduleModal = ({ item, preloadedBooking, onClose, onRescheduled, onBackToDetails }) => {
    const [loading, setLoading] = useState(!preloadedBooking);
    const [registration, setRegistration] = useState(preloadedBooking || null);
    const [locationId, setLocationId] = useState(preloadedBooking?.LocationId || "");

    const [visitDate, setVisitDate] = useState(new Date());
    const visitDateRef = useRef(null);
    const [slots, setSlots] = useState([]);
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [activeSlotPeriod, setActiveSlotPeriod] = useState("Morning");
    const [rescheduleReason, setRescheduleReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const isDirty = !!selectedSlot || !!rescheduleReason;
    useUnsavedChangesGuard(isDirty && !isSubmitting, onClose);

    const handleCloseClick = () => {
        if (isSubmitting) return;
        if (isDirty) {
            setShowConfirmationModal(true);
        } else {
            onClose();
        }
    };

    useEffect(() => {
        if (preloadedBooking) return;
        HVTRegistrationService.GetBookingDetailsByBookingId(item.BookingId)
            .then(({ data }) => {
                const booking = data?.Data?.BookingDetails?.[0];
                if (!data?.IsSuccess || !booking) {
                    toast.error("HVT Error: Unable to load booking details.");
                    onClose();
                    return;
                }
                setLocationId(booking.LocationId || "");
                setRegistration(booking);
            })
            .catch(() => {
                toast.error("HVT Error: Something went wrong while loading booking details.");
                onClose();
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!registration) return;
        setIsLoadingSlots(true);
        setSlots([]);
        setSelectedSlot(null);

        const payload = {
            Pick_Up_Date: formatDateOnly(visitDate),
            Location: registration.Location,
            Pincode: registration.Pincode,
            LocationID: locationId,
            Gender: toPhleboGender(registration.Gender),
        };

        HVTRegistrationService.GetPhleboAvailableLocationDecrypt(payload)
            .then(({ data }) => {
                setSlots(data?.IsSuccess && data?.Data?.GetCircleLocation?.length ? data.Data.GetCircleLocation : []);
            })
            .catch(() => setSlots([]))
            .finally(() => setIsLoadingSlots(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registration, visitDate, locationId]);

    useEffect(() => {
        const groups = groupSlotsByPeriod(slots);
        const firstNonEmpty = SLOT_PERIODS.find((p) => groups[p].length > 0);
        setActiveSlotPeriod(firstNonEmpty || "Morning");
    }, [slots]);

    const handleConfirmReschedule = () => {
        if (!selectedSlot) {
            toast.error("Please select a new slot.");
            return;
        }
        if (!rescheduleReason) {
            toast.error("Please select a reason for rescheduling.");
            return;
        }

        setIsSubmitting(true);

        const dateTime = `${formatDateOnly(visitDate)} ${selectedSlot.Slot_TIME}`;

        HVTRegistrationService.getGroupSiblings(item.BookingId)
            .then((siblings) => {
                const targets = siblings.length ? siblings : [{ BookingId: item.BookingId, PID: item.PatientId }];
                return Promise.all(
                    targets.map((p) => {
                        const payload = {
                            BookingID: p.BookingId,
                            Scheduledate: dateTime,
                            Bookingdate: dateTime,
                            Remarks: "1",
                            THPatientID: p.PID,
                            RescheduleReason: rescheduleReason,
                            UserId: null,
                            PartnerCode: null,
                        };
                        return HVTRegistrationService.SaveReschedule(payload);
                    })
                );
            })
            .then((results) => {
                if (results.some((r) => !r?.data?.IsSuccess)) {
                    toast.error("Some patients in this booking could not be rescheduled.");
                } else {
                    toast.success("Booking rescheduled successfully.");
                }
                onRescheduled();
                onClose();
            })
            .catch(() => toast.error("Something went wrong while rescheduling."))
            .finally(() => setIsSubmitting(false));
    };

    const slotGroups = groupSlotsByPeriod(slots);

    return (
        <>
            <div className="modal-backdrop">
                <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-dialog-centered hvt-modal-dialog hvt-modal-dialog-wide" role="document">
                        <div className="modal-content" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                            <div className="hvt-wizard-modal">
                                <div className="modal-header px-3 py-2">
                                    <h5 className="modal-title">Reschedule Booking{item?.BookingId ? ` (${item.BookingId})` : ""}</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={handleCloseClick} disabled={isSubmitting}></button>
                                </div>

                                {loading ? (
                                    <div className="text-center py-4"><FaSpinner className="fa-spin" /></div>
                                ) : (
                                    <>
                                        <div className="hvt-wizard-scroll">
                                            <div className="hvt-step-body">
                                                <div className="hvt-quick-section">
                                                    <div className="hvt-quick-section-title required"><Clock size={13} /> New Pickup Slot</div>

                                                    <div className="hvt-field mb-2" style={{ maxWidth: 200 }}>
                                                        <label className="required">Slot Date</label>
                                                        <div className="position-relative">
                                                            <DatePicker
                                                                ref={visitDateRef}
                                                                portalId="hvt-datepicker-portal"
                                                                className="form-control"
                                                                selected={visitDate}
                                                                onChange={setVisitDate}
                                                                dateFormat="dd/MM/yyyy"
                                                                minDate={new Date()}
                                                                maxDate={MAX_SLOT_DATE}
                                                                placeholderText="DD/MM/YYYY"
                                                            />
                                                            <Calendar size={16} style={{ position: "absolute", right: 12, top: 10, cursor: "pointer" }} onClick={() => visitDateRef.current?.setFocus()} />
                                                        </div>
                                                    </div>

                                                    {isLoadingSlots && <FaSpinner className="fa-spin" />}
                                                    {!isLoadingSlots && slots.length === 0 && (
                                                        <small className="text-muted">No slots available for this date.</small>
                                                    )}
                                                    {!isLoadingSlots && slots.length > 0 && (
                                                        <>
                                                            <div className="hvt-slot-tabs">
                                                                {SLOT_PERIODS.filter((period) => slotGroups[period].length > 0).map((period) => (
                                                                    <button
                                                                        key={period}
                                                                        type="button"
                                                                        className={`hvt-slot-tab ${activeSlotPeriod === period ? "active" : ""}`}
                                                                        onClick={() => setActiveSlotPeriod(period)}
                                                                    >
                                                                        {period}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <div className="hvt-slot-card-grid">
                                                                {(slotGroups[activeSlotPeriod] || []).map((slot, idx) => {
                                                                    const isAvailable = slot.Available === "A";
                                                                    const isSelected = selectedSlot?.Slot_TIME === slot.Slot_TIME;
                                                                    const [timeMain, timeMer] = formatSlotTimeDisplay(slot.Slot_TIME).split(" ");
                                                                    return (
                                                                        <button
                                                                            key={idx}
                                                                            type="button"
                                                                            className={`hvt-slot-card ${isSelected ? "hvt-slot-selected" : ""}`}
                                                                            disabled={!isAvailable}
                                                                            onClick={() => setSelectedSlot(slot)}
                                                                        >
                                                                            <div className="hvt-slot-card-time">{timeMain}</div>
                                                                            <div className="hvt-slot-card-mer">{timeMer}</div>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                <div className="hvt-quick-section">
                                                    <div className="hvt-quick-section-title required">Reschedule Reason</div>
                                                    <div className="hvt-field">
                                                        <label className="required">Reason</label>
                                                        <select
                                                            className="form-select"
                                                            value={rescheduleReason}
                                                            onChange={(e) => setRescheduleReason(e.target.value)}
                                                        >
                                                            <option value="">Select a reason</option>
                                                            {RESCHEDULE_REASONS.map((reason) => (
                                                                <option key={reason} value={reason}>{reason}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>

                                        <div className="hvt-wizard-footer d-flex justify-content-between gap-2">
                                            <button
                                                type="button"
                                                className="btn hvt-slot-btn px-3"
                                                style={{ borderRadius: 6, minWidth: "auto" }}
                                                onClick={() => onBackToDetails?.()}
                                            >
                                                Back to Details
                                            </button>
                                            <button
                                                type="button"
                                                className="btn custom-sumbitbtn d-flex align-items-center gap-1"
                                                disabled={isSubmitting || !selectedSlot || !rescheduleReason}
                                                onClick={handleConfirmReschedule}
                                            >
                                                {isSubmitting ? <FaSpinner className="fa-spin" size={13} /> : null}
                                                Confirm Reschedule
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showConfirmationModal && (
                <CommonConfirmationModal
                    setShowConfirmationModal={setShowConfirmationModal}
                    description="You have an unsaved reschedule. Are you sure you want to close?"
                    onConfirm={() => {
                        setShowConfirmationModal(false);
                        onClose();
                    }}
                />
            )}
        </>
    );
};

export default HVTRescheduleModal;
